import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { usageEventsAPI, UsageEventRow } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import PageHero from '../components/Common/PageHero';
import MetricCard from '../components/Common/MetricCard';
import DataGrid from '../components/Common/DataGrid';

type Stats = Awaited<ReturnType<typeof usageEventsAPI.stats>>;

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_flight', label: 'Collected, awaiting ack' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
  { value: 'expired', label: 'Expired' },
];

const EVENT_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'usage.threshold_50', label: '50% reached' },
  { value: 'usage.threshold_75', label: '75% reached' },
  { value: 'usage.threshold_100', label: '100% reached' },
  { value: 'topup.applied', label: 'Topup applied' },
  { value: 'service.reactivated', label: 'Service reactivated' },
];

const EVENT_LABELS = Object.fromEntries(EVENT_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]));

const DELIVERY_OPTIONS = [
  { value: '', label: 'All channel results' },
  { value: 'email:sent', label: 'Email sent' },
  { value: 'whatsapp:sent', label: 'WhatsApp sent' },
  { value: 'email:failed', label: 'Email failed' },
  { value: 'whatsapp:failed', label: 'WhatsApp failed' },
  { value: 'email:skipped', label: 'Email skipped' },
  { value: 'whatsapp:skipped', label: 'WhatsApp skipped' },
];

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  in_flight: 'bg-blue-100 text-blue-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-rose-100 text-rose-700',
  expired: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_flight: 'Awaiting ack',
  delivered: 'Delivered',
  failed: 'Failed',
  expired: 'Expired',
};

const CHANNEL_STYLES: Record<string, string> = {
  sent: 'bg-emerald-100 text-emerald-700',
  queued: 'bg-amber-100 text-amber-700',
  failed: 'bg-rose-100 text-rose-700',
  skipped: 'bg-gray-100 text-gray-600',
};

const ChannelStatus: React.FC<{ row: UsageEventRow; channel: 'email' | 'whatsapp' }> = ({ row, channel }) => {
  const result = row.deliveryStatus?.[channel];
  if (!result) return <span className="text-xs text-gray-400">Not reported</span>;
  return (
    <div className="space-y-1">
      <span className={`inline-flex rounded-lg px-2.5 py-1 text-xs font-semibold capitalize ${CHANNEL_STYLES[result.status] || CHANNEL_STYLES.skipped}`}>
        {result.status}
      </span>
      {result.recipient ? <div className="text-xs text-gray-500">{result.recipient}</div> : null}
      {result.detail ? <div className="max-w-[180px] break-words text-xs text-gray-500">{result.detail}</div> : null}
    </div>
  );
};

const formatDate = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '-';

const UsageEvents: React.FC = () => {
  const { user } = useAuthStore();
  const canRequeue = Boolean(user?.hasAdminAccess);

  const [events, setEvents] = useState<UsageEventRow[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });
  const [statusFilter, setStatusFilter] = useState('');
  const [eventFilter, setEventFilter] = useState('');
  const [lineFilter, setLineFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [selected, setSelected] = useState<UsageEventRow | null>(null);
  const [requeuing, setRequeuing] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const [deliveryChannel, deliveryStatus] = deliveryFilter.split(':');
        const [list, summary] = await Promise.all([
          usageEventsAPI.list({
            page,
            limit: 50,
            status: statusFilter || undefined,
            event: eventFilter || undefined,
            serviceLine: lineFilter.trim() || undefined,
            deliveryChannel: (deliveryChannel || undefined) as 'email' | 'whatsapp' | undefined,
            deliveryStatus: (deliveryStatus || undefined) as 'sent' | 'failed' | 'skipped' | undefined,
          }),
          usageEventsAPI.stats(),
        ]);
        setEvents(list.data || []);
        setPagination(list.pagination);
        setStats(summary);
      } catch (error) {
        console.error('Error loading usage events:', error);
        toast.error('Failed to load usage events');
        setEvents([]);
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, eventFilter, lineFilter, deliveryFilter],
  );

  useEffect(() => {
    // Debounce so typing a service line does not fire a request per keystroke.
    const timer = setTimeout(() => load(1), 300);
    return () => clearTimeout(timer);
  }, [load]);

  const handleRequeue = async (row: UsageEventRow) => {
    try {
      setRequeuing(row.eventId);
      await usageEventsAPI.requeue(row.eventId);
      toast.success(`Event for ${row.serviceLine} put back in the queue`);
      await load(pagination.page);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to requeue event');
    } finally {
      setRequeuing(null);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const [deliveryChannel, deliveryStatus] = deliveryFilter.split(':');
      const params = {
        limit: 100,
        status: statusFilter || undefined,
        event: eventFilter || undefined,
        serviceLine: lineFilter.trim() || undefined,
        deliveryChannel: (deliveryChannel || undefined) as 'email' | 'whatsapp' | undefined,
        deliveryStatus: (deliveryStatus || undefined) as 'sent' | 'failed' | 'skipped' | undefined,
      };

      const first = await usageEventsAPI.list({ ...params, page: 1 });
      const allEvents = [...(first.data || [])];
      for (let page = 2; page <= first.pagination.totalPages; page += 1) {
        const next = await usageEventsAPI.list({ ...params, page });
        allEvents.push(...(next.data || []));
      }

      if (!allEvents.length) {
        toast.error('There are no usage events matching the current filters');
        return;
      }

      const rows = allEvents.map((row) => {
        const email = row.deliveryStatus?.email;
        const whatsapp = row.deliveryStatus?.whatsapp;
        return {
          'Event ID': row.eventId,
          'Queued At': formatDate(row.createdAt),
          Event: EVENT_LABELS[row.event] || row.event,
          'Service Line': row.serviceLine,
          'Kit Number': row.payload?.kitNumber || '',
          'Usage Percentage': row.payload?.usagePercentage ?? '',
          'Usage GB': row.payload?.usageGB ?? '',
          'Total GB': row.payload?.totalGB ?? '',
          'Remaining GB': row.payload?.remainingGB ?? '',
          'Queue Status': STATUS_LABELS[row.status] || row.status,
          Attempts: row.attempts,
          'Collected By': row.claimedBy || '',
          'Delivered At': formatDate(row.deliveredAt),
          'Queue Error': row.lastError || '',
          'Email Status': email?.status || 'not reported',
          'Email Recipient': email?.recipient || '',
          'Email Provider Message ID': email?.providerMessageId || '',
          'Email Status At': formatDate(email?.updatedAt),
          'Email Detail': email?.detail || '',
          'WhatsApp Status': whatsapp?.status || 'not reported',
          'WhatsApp Recipient': whatsapp?.recipient || '',
          'WhatsApp Provider Message ID': whatsapp?.providerMessageId || '',
          'WhatsApp Status At': formatDate(whatsapp?.updatedAt),
          'WhatsApp Detail': whatsapp?.detail || '',
          'Event Payload': JSON.stringify(row.payload || {}),
        };
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = Object.keys(rows[0]).map((key) => ({
        wch: Math.min(60, Math.max(key.length + 2, 16)),
      }));
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Usage Events');
      XLSX.writeFile(workbook, `myaura-usage-events-${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${allEvents.length} usage event${allEvents.length === 1 ? '' : 's'}`);
    } catch (error) {
      console.error('Usage events export failed:', error);
      toast.error('Failed to export usage events');
    } finally {
      setExporting(false);
    }
  };

  const counts = stats?.counts;

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'Customer notifications', tone: 'cyan' }}
        title="Usage Events"
        description="Events queued for MyAura when a service line reaches 50%, 75% or 100%, gets a topup, or is reactivated. MyAura collects them and notifies the customer."
        stats={[{ icon: 'fas fa-list-alt', label: `${pagination.total} events` }]}
        actions={
          <>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="rounded-2xl bg-emerald-400 px-6 py-3 font-semibold text-slate-950 hover:bg-emerald-300 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className={`fas ${exporting ? 'fa-spinner animate-spin' : 'fa-file-excel'} mr-2`}></i>
              {exporting ? 'Exporting…' : 'Export Excel'}
            </button>
            <button
              onClick={() => load(pagination.page)}
              className="rounded-2xl bg-white px-6 py-3 font-semibold text-slate-900 hover:bg-slate-100"
            >
              <i className="fas fa-sync-alt mr-2"></i>
              Refresh
            </button>
          </>
        }
      />

      {stats && !stats.enabled && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          Usage events are switched off, so no new events are being queued. Set <code className="font-semibold">USAGE_EVENTS_ENABLED=true</code> on
          the backend and restart it to turn them on.
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Waiting"
          value={counts ? counts.pending : '-'}
          hint={stats?.oldestPendingAt ? `Oldest since ${formatDate(stats.oldestPendingAt)}` : 'Nothing waiting for collection'}
          icon="fas fa-hourglass-half"
          accent="amber"
        />
        <MetricCard
          label="Collected, awaiting ack"
          value={counts ? counts.in_flight : '-'}
          hint="Handed to MyAura, not yet acknowledged"
          icon="fas fa-inbox"
          accent="blue"
        />
        <MetricCard label="Delivered" value={counts ? counts.delivered : '-'} hint="Acknowledged by MyAura; channel results are additional" icon="fas fa-check-circle" accent="green" />
        <MetricCard
          label="Failed or expired"
          value={counts ? counts.failed + counts.expired : '-'}
          hint="Gave up retrying, or never collected in time"
          icon="fas fa-exclamation-triangle"
          accent="red"
        />
      </div>

      <DataGrid
        title="Event queue"
        description={`${events.length} of ${pagination.total} events, newest first`}
        loading={loading}
        emptyText="No usage events found."
        rows={events}
        toolbar={
          <div className="flex flex-col gap-3 lg:flex-row">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 min-w-[180px]"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 min-w-[180px]"
            >
              {EVENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={deliveryFilter}
              onChange={(e) => setDeliveryFilter(e.target.value)}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 min-w-[180px]"
            >
              {DELIVERY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <i className="fas fa-search text-gray-400"></i>
              </div>
              <input
                type="text"
                value={lineFilter}
                onChange={(e) => setLineFilter(e.target.value)}
                placeholder="Filter by service line..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              />
            </div>
          </div>
        }
        columns={[
          {
            key: 'created',
            header: 'Queued',
            className: 'whitespace-nowrap',
            render: (row) => <span className="text-xs text-gray-600">{formatDate(row.createdAt)}</span>,
          },
          {
            key: 'event',
            header: 'Event',
            render: (row) => <span className="text-sm font-medium text-gray-900">{EVENT_LABELS[row.event] || row.event}</span>,
          },
          {
            key: 'serviceLine',
            header: 'Service line',
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-gray-900">{row.serviceLine}</div>
                {row.payload?.kitNumber ? <div className="text-xs text-gray-500">{row.payload.kitNumber}</div> : null}
              </div>
            ),
          },
          {
            key: 'usage',
            header: 'Usage',
            render: (row) => (
              <div className="text-xs text-gray-600">
                <div className="font-semibold text-gray-900">{row.payload?.usagePercentage ?? '-'}%</div>
                <div>
                  {row.payload?.remainingGB ?? '-'} GB left of {row.payload?.totalGB ?? '-'} GB
                </div>
              </div>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (row) => (
              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${STATUS_STYLES[row.status] || 'bg-gray-100 text-gray-700'}`}>
                {STATUS_LABELS[row.status] || row.status}
              </span>
            ),
          },
          {
            key: 'emailStatus',
            header: 'Email',
            render: (row) => <ChannelStatus row={row} channel="email" />,
          },
          {
            key: 'whatsappStatus',
            header: 'WhatsApp',
            render: (row) => <ChannelStatus row={row} channel="whatsapp" />,
          },
          {
            key: 'attempts',
            header: 'Attempts',
            render: (row) => <span className="text-xs text-gray-600">{row.attempts}</span>,
          },
          {
            key: 'detail',
            header: 'Detail',
            render: (row) => (
              <div className="max-w-xs text-xs text-gray-600">
                {row.status === 'delivered' && <div>Delivered {formatDate(row.deliveredAt)}</div>}
                {row.status === 'in_flight' && <div>Lease ends {formatDate(row.lockedUntil)}</div>}
                {row.status === 'pending' && new Date(row.availableAt).getTime() > Date.now() && (
                  <div>Retry after {formatDate(row.availableAt)}</div>
                )}
                {row.claimedBy ? <div>Collected by {row.claimedBy}</div> : null}
                {row.lastError ? <div className="text-rose-600 break-words">{row.lastError}</div> : null}
              </div>
            ),
          },
          {
            key: 'actions',
            header: '',
            className: 'whitespace-nowrap',
            render: (row) => (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelected(row)}
                  className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
                >
                  View
                </button>
                {canRequeue && (row.status === 'failed' || row.status === 'expired') && (
                  <button
                    onClick={() => handleRequeue(row)}
                    disabled={requeuing === row.eventId}
                    className="px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100 disabled:opacity-50"
                  >
                    {requeuing === row.eventId ? 'Requeuing...' : 'Retry'}
                  </button>
                )}
              </div>
            ),
          },
        ]}
      />

      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <button
            onClick={() => load(pagination.page - 1)}
            disabled={!pagination.hasPrev}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="fas fa-chevron-left mr-2"></i>
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => load(pagination.page + 1)}
            disabled={!pagination.hasNext}
            className="px-4 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-xl hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <i className="fas fa-chevron-right ml-2"></i>
          </button>
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setSelected(null)}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-black text-gray-900">{EVENT_LABELS[selected.event] || selected.event}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {selected.serviceLine} - event {selected.eventId}
                </p>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Exactly what MyAura receives</p>
            <pre className="mt-2 max-h-96 overflow-auto rounded-xl bg-gray-900 p-4 text-xs text-gray-100">
              {JSON.stringify(selected.payload, null, 2)}
            </pre>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Customer notification channels</p>
            <pre className="mt-2 max-h-52 overflow-auto rounded-xl bg-gray-900 p-4 text-xs text-gray-100">
              {JSON.stringify(selected.deliveryStatus || {}, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageEvents;
